import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowUpRight, ShieldCheck, ArrowRight, Check, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import NetworkGraph from "@/components/NetworkGraph";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { services, pillars, testimonials, stats, marqueeItems } from "@/data/content";

function Stat({ value, suffix, text, label, delay }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView || value === null) return;
        const controls = animate(0, value, {
            duration: 1.8,
            delay,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value, delay]);

    return (
        <div ref={ref}>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#161616]">
                {value === null ? text : `${display}${suffix}`}
            </p>
            <p className="mt-2 text-sm text-[#77776E] leading-snug">{label}</p>
        </div>
    );
}

const heroLines = [
    "Talent intelligence &",
    "recruitment support for",
    { text: "every industry,", accent: true },
    { text: "every hiring need.", accent: false },
];

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const yGraph = useTransform(scrollYProgress, [0, 1], [0, 90]);

    const featured = services.filter((s) => s.featured);
    const rest = services.filter((s) => !s.featured);

    return (
        <div>
            <Seo
                title="Vantage Crest Research | Talent Intelligence, Delivered."
                description="Talent intelligence and recruitment support for every industry, every hiring need — CV formatting, lead generation, executive search and talent research. Mumbai, India."
            />

            <section ref={heroRef} className="relative overflow-hidden bg-[#F7F5F1]">
                <div className="absolute inset-0 grid-lines-light pointer-events-none" aria-hidden="true" />
                <div
                    className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(201,162,75,0.16) 0%, rgba(18,18,18,0.08) 45%, transparent 70%)",
                    }}
                    aria-hidden="true"
                />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-20 lg:pt-44 lg:pb-28 grid lg:grid-cols-12 gap-14 items-start min-h-[92vh]">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.8 }}
                            className="font-mono-accent text-xs tracking-[0.28em] uppercase text-[#C9A24B]"
                        >
                            Vantage Crest Research — Talent Intelligence, Delivered.
                        </motion.p>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] text-[#141414] mt-7">
                            {heroLines.map((line, i) => (
                                <span key={i} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className={`block ${line.accent ? "text-[#C9A24B]" : ""}`}
                                        initial={{ y: "112%" }}
                                        animate={{ y: "0%" }}
                                        transition={{
                                            duration: 1,
                                            delay: 0.22 + i * 0.12,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                    >
                                        {line.text || line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-8 text-lg text-[#4E4E48] leading-relaxed max-w-xl"
                        >
                            From same-week CV formatting and lead generation to confidential
                            executive search — one research-led partner for every hiring
                            problem you'll ever have.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 flex flex-wrap gap-4"
                        >
                            <Link
                                to="/services#cv-formatting"
                                data-testid="hero-cta-cv-formatting"
                                className="inline-flex items-center gap-2 bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C] font-bold px-7 py-4 text-sm transition-colors duration-200 active:scale-[0.98]"
                            >
                                Get Your CV Professionally Formatted <ArrowUpRight size={16} />
                            </Link>
                            <Link
                                to="/services#lead-generation"
                                data-testid="hero-cta-lead-generation"
                                className="inline-flex items-center gap-2 bg-[#161616] hover:bg-[#242424] text-[#F6F4EF] font-bold px-7 py-4 text-sm transition-colors duration-200 active:scale-[0.98]"
                            >
                                Generate Quality Leads <ArrowUpRight size={16} />
                            </Link>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.25 }}
                            className="mt-12 font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#77776E]"
                        >
                            MUMBAI · INDIA — SERVING EVERY SECTOR · EST. BY OPERATORS, NOT GENERALISTS
                        </motion.p>
                    </div>

                    <motion.div
                        style={{ y: yGraph }}
                        initial={{ opacity: 0, scale: 0.96, rotate: 0 }}
                        animate={{ opacity: 1, scale: 1, rotate: 1 }}
                        transition={{ delay: 0.55, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="relative hidden lg:block lg:col-span-5 lg:mt-20"
                    >
                        <div className="border border-[#161616]/25 bg-[#171717] p-6 shadow-[0_30px_60px_-20px_rgba(6,6,6,0.45)]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#D8BC7A]">
                                    Talent Network Visualiser
                                </span>
                                <span className="font-mono-accent text-[10px] tracking-[0.22em] text-[#A6A69C]">
                                    LIVE · GLOBAL
                                </span>
                            </div>
                            <NetworkGraph className="w-full h-auto" />
                        </div>
                        <p className="mt-4 text-right font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#77776E]">
                            FIG. 01 — THE MARKET, MAPPED
                        </p>
                    </motion.div>
                </div>
            </section>

            <section
                data-testid="featured-services-strip"
                className="relative bg-[#0C0C0C] py-24 overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <Eyebrow dark>Start Here</Eyebrow>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F6F4EF] mt-4 leading-[1.05]">
                                Two fast ways to
                                <br />
                                work with us.
                            </h2>
                        </div>
                        <p className="text-[#A6A69C] text-sm leading-relaxed max-w-xs md:text-right">
                            Low-commitment, high-impact entry points — delivered in days,
                            not months.
                        </p>
                    </Reveal>
                    <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
                        {featured.map((s, i) => (
                            <Reveal key={s.id} delay={i * 0.12} className={i === 1 ? "lg:translate-y-10" : ""}>
                                <div
                                    data-testid={`featured-card-${s.id}`}
                                    className="group bg-[#F7F5F1] p-8 sm:p-11 h-full flex flex-col border-t-4 border-[#C9A24B] hover:-translate-y-1 transition-transform duration-300"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="w-12 h-12 bg-[#161616] flex items-center justify-center">
                                            <s.icon size={22} className="text-[#D8BC7A]" strokeWidth={1.5} />
                                        </span>
                                        <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#C9A24B] border border-[#C9A24B]/50 px-2.5 py-1">
                                            {s.badge}
                                        </span>
                                    </div>
                                    <h3 className="mt-7 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141414]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-4 text-sm sm:text-base text-[#4E4E48] leading-relaxed">
                                        {s.description}
                                    </p>
                                    <ul className="mt-6 space-y-2.5">
                                        {s.deliverables.slice(0, 3).map((d, j) => (
                                            <li key={j} className="flex items-start gap-2.5 text-sm text-[#4E4E48]">
                                                <Check size={15} className="mt-0.5 shrink-0 text-[#C9A24B]" />
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-8 pt-6 border-t border-[#E7E4DC] flex items-center justify-between mt-auto">
                                        <Link
                                            to={`/services#${s.id}`}
                                            data-testid={`featured-cta-${s.id}`}
                                            className={`inline-flex items-center gap-2 font-bold text-sm px-6 py-3 transition-colors duration-200 active:scale-[0.98] ${
                                                i === 0
                                                    ? "bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C]"
                                                    : "bg-[#161616] hover:bg-[#242424] text-[#F6F4EF]"
                                            }`}
                                        >
                                            {i === 0 ? "Get Your CV Formatted" : "Generate Quality Leads"}
                                            <ArrowUpRight size={15} />
                                        </Link>
                                        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#77776E]">
                                            <Zap size={13} className="text-[#C9A24B]" />
                                            {i === 0 ? "From 24 hours" : "On-demand batches"}
                                        </span>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#F7F5F1] py-24 lg:pt-36">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <Eyebrow>The Full Range</Eyebrow>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#141414] mt-4 leading-[1.05]">
                                Six more research-grade services.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-2 text-sm font-bold text-[#161616] hover:text-[#C9A24B] transition-colors"
                            data-testid="services-view-all-link"
                        >
                            Explore all services <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                    <div
                        data-testid="services-preview-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E4DC] border border-[#E7E4DC]"
                    >
                        {rest.map((s, i) => (
                            <Reveal key={s.id} delay={(i % 3) * 0.08} className="h-full">
                                <Link
                                    to={`/services#${s.id}`}
                                    data-testid={`service-card-${s.id}`}
                                    className="group flex flex-col bg-white hover:bg-[#F7F5F1] p-8 h-full transition-colors duration-300"
                                >
                                    <div className="flex items-center justify-between">
                                        <s.icon size={22} className="text-[#161616]" strokeWidth={1.5} />
                                        <span className="font-mono-accent text-[10px] tracking-[0.2em] text-[#77776E]">
                                            {s.code}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 font-display font-bold text-xl text-[#141414] leading-snug group-hover:text-[#161616] transition-colors">
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#77776E] leading-relaxed flex-1">
                                        {s.short}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-[#C9A24B] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        Learn more <ArrowRight size={13} />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section
                data-testid="stats-strip-container"
                className="bg-white border-y border-[#E7E4DC]"
            >
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
                    {stats.map((s, i) => (
                        <Stat key={i} {...s} delay={i * 0.12} />
                    ))}
                </div>
            </section>

            <section
                data-testid="why-vantage-crest-section"
                className="relative bg-[#171717] py-24 overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-14">
                        <Eyebrow dark>Why Vantage Crest Research</Eyebrow>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F6F4EF] mt-4 leading-[1.05]">
                            Built like a research house.
                            <br />
                            Delivered like a partner.
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {pillars.map((p, i) => (
                            <Reveal key={p.num} delay={i * 0.1} className="h-full">
                                <div className="bg-[#171717] p-8 h-full hover:bg-[#242424] transition-colors duration-300">
                                    <span className="font-mono-accent text-sm text-[#D8BC7A] tracking-widest">
                                        {p.num}
                                    </span>
                                    <h3 className="mt-5 font-display font-bold text-lg text-[#F6F4EF]">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#A6A69C] leading-relaxed">
                                        {p.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <div className="marquee bg-[#C9A24B] py-4 overflow-hidden" aria-hidden="true">
                <div className="marquee-track">
                    {[...marqueeItems, ...marqueeItems].map((item, i) => (
                        <span
                            key={i}
                            className="flex items-center gap-6 px-6 font-display font-bold text-lg text-[#0C0C0C] whitespace-nowrap"
                        >
                            {item}
                            <span className="w-1.5 h-1.5 rotate-45 bg-[#0C0C0C]/50" />
                        </span>
                    ))}
                </div>
            </div>

            <section data-testid="testimonials-section" className="bg-[#F7F5F1] py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-14">
                        <Eyebrow>Client Voices</Eyebrow>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#141414] mt-4 leading-[1.05]">
                            Trusted by teams who move fast.
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((t, i) => (
                            <Reveal key={i} delay={i * 0.1} className={i === 1 ? "md:translate-y-6" : ""}>
                                <figure className="bg-white border border-[#E7E4DC] p-8 h-full flex flex-col hover:border-[#C9A24B]/60 transition-colors duration-300">
                                    <span className="font-display text-5xl font-bold text-[#C9A24B]/50 leading-none">
                                        &ldquo;
                                    </span>
                                    <blockquote className="mt-2 text-[#4E4E48] text-sm leading-relaxed flex-1">
                                        {t.quote}
                                    </blockquote>
                                    <figcaption className="mt-6 pt-5 border-t border-[#E7E4DC]">
                                        <p className="font-bold text-sm text-[#141414]">{t.name}</p>
                                        <p className="text-xs text-[#77776E] mt-1">
                                            {t.title} · {t.company}
                                        </p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white pb-24 px-5 sm:px-8">
                <Reveal className="max-w-5xl mx-auto">
                    <div
                        data-testid="data-protection-callout"
                        className="border-l-4 border border-[#C9A24B]/40 bg-[#F7F5F1] p-8 sm:p-10 flex flex-col sm:flex-row items-start gap-6"
                    >
                        <span className="shrink-0 w-12 h-12 bg-[#161616] flex items-center justify-center">
                            <ShieldCheck size={22} className="text-[#D8BC7A]" strokeWidth={1.5} />
                        </span>
                        <div>
                            <h3 className="font-display text-xl font-bold text-[#141414]">
                                Data Protection & Confidentiality
                            </h3>
                            <p className="mt-3 text-sm text-[#4E4E48] leading-relaxed">
                                We follow strict data protection and confidentiality protocols
                                in every engagement, ensuring client and candidate information
                                is always handled securely. Discretion is not a feature of our
                                service — it is the foundation of it.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
